import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import useApiErrorHandler from '../hooks/useApiErrorHandler';
import LoadingSpinner from './common/LoadingSpinner';
import Alert from './common/Alert';
import dashboardService from '../services/DashboardService';
import usuarioService from '../services/UsuarioService';
import ActiveRegionalBadge from './common/ActiveRegionalBadge';
import PasswordInputWithToggle from './common/PasswordInputWithToggle';
import ProcesosMenu from './common/ProcesosMenu';
import SolicitudesProcesosBadge from './common/SolicitudesProcesosBadge';
import './NewDashboard.css';
import {
  isGptClassicEnabled as hasGptClassicFeature,
  isOpenAIAssistantEnabled as hasOpenAIAssistantFeature,
  isRagFlowAssistantEnabled as hasRagFlowAssistantFeature
} from '../utils/adminMenu';
import useRegionalFeatureFlags from '../hooks/useRegionalFeatureFlags';
import ProfessorMenuOptions from './sidebar/ProfessorMenuOptions';
import { getProfessorMenuItems, navigateProfessorMenuItem } from './sidebar/menuConfig';

const isProfessorUser = (currentUser) => {
  if (!currentUser) return false;
  const roleName = (currentUser.roleName || currentUser.nombreRol || currentUser.role || '').toLowerCase();
  return roleName === 'profesor' || roleName.includes('profesor') || roleName.includes('profe');
};

const NewDashboard = () => {
  const { user, logout, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { handleApiError } = useApiErrorHandler();
  const isProfesor = isProfessorUser(user);
  const { featureFlags } = useRegionalFeatureFlags(user, { enabled: !authLoading });
  const isGptClassicEnabled = hasGptClassicFeature(featureFlags);
  const isOpenAIAssistantEnabled = hasOpenAIAssistantFeature(featureFlags);
  const isRagFlowAssistantEnabled = hasRagFlowAssistantFeature(featureFlags);
  
  const [estadisticas, setEstadisticas] = useState(null);
  const [datosFormateados, setDatosFormateados] = useState(null);
  const [tendencias, setTendencias] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'error', title: '' });
  const [activeMenuItem, setActiveMenuItem] = useState('dashboard');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  
  
  // Estados paral modal de cambio de password
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [canCloseModal, setCanCloseModal] = useState(true);
  
  // Ref para evitar múltiples llamadas en React Strict Mode
  const isDataLoaded = useRef(false);
  const hasLoggedRole = useRef(false);

  useEffect(() => {
    if (user && !hasLoggedRole.current) {
      console.group('[NewDashboard] Debug Rol');
      console.log('Usuario:', user);
      console.log('roleName:', user?.roleName);
      console.log('rol (string):', user?.role);
      console.log('roleId:', user?.roleId);
      console.log('isProfesor flag:', isProfesor);
      console.groupEnd();
      hasLoggedRole.current = true;
    }
  }, [user, isProfesor]);

  // Cargar estadísticas al montar el componente
  useEffect(() => {
    if (authLoading) return;

    if (!isProfesor) {
      setIsLoading(false);
      return;
    }

    if (!isDataLoaded.current) {
      isDataLoaded.current = true;
      cargarEstadisticas();
    }
  }, [isProfesor, authLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (location.state?.deniedOpenAIAssistantFeature) {
      setAlert({
        show: true,
        title: 'Funcionalidad deshabilitada',
        message: 'El feature flag de Asistentes OpenAI no está habilitado para esta regional.',
        type: 'warning'
      });
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }

    if (location.state?.deniedRagFlowAssistantFeature) {
      setAlert({
        show: true,
        title: 'Funcionalidad deshabilitada',
        message: 'El feature flag de Asistentes AVE no esta habilitado para esta regional.',
        type: 'warning'
      });
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }

    if (location.state?.deniedCreateAssistant) {
      setAlert({
        show: true,
        title: 'Permiso denegado',
        message: 'No tienes permisos para crear asistentes. Contacta al administrador.',
        type: 'warning'
      });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  const cargarEstadisticas = async () => {
    if (authLoading) return;
    if (!isProfesor) {
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      
      // Obtener estadísticas del backend
      const estadisticasData = await dashboardService.getEstadisticas();
      setEstadisticas(estadisticasData);
      
      // Formatear datos para gráficos
      const datosFormateados = dashboardService.formatearDatosParaGraficos(estadisticasData);
      setDatosFormateados(datosFormateados);
      
      // Calcular tendencias
      const tendenciasData = dashboardService.calcularTendencias(datosFormateados);
      setTendencias(tendenciasData);
      
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
      
      try {
        // Usar el manejador de errores personalizado para errores 401
        handleApiError(error);
      } catch (handledError) {
        // Si el error no es 401, mostrar mensaje de error
        setAlert({
          show: true,
          message: handledError.message || error.message || 'Error al cargar las estadísticas del dashboard',
          type: 'error'
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
  };


  if (authLoading) {
    return (
      <div className="dashboard-container">
        <div className="loading-container">
          <LoadingSpinner message="Verificando permisos..." />
        </div>
      </div>
    );
  }

  if (!isProfesor) {
    return (
      <div className="dashboard-container">
        <div
          style={{
            maxWidth: '640px',
            margin: '0 auto',
            padding: '2rem',
            backgroundColor: '#fff',
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.1)',
            textAlign: 'center'
          }}
        >
          <h2>Acceso restringido</h2>
          <p>Este dashboard está disponible únicamente para usuarios con rol Profesor.</p>
        </div>
      </div>
    );
  }

  // Funciones para manejar el modal de cambio de password
  const handleUpdatePassword = () => {
    setShowPasswordModal(true);
    setShowUserMenu(false); // Cerrar el menú de usuario
  };

  const handleClosePasswordModal = () => {
    if (!canCloseModal) {
      return;
    }
    
    setShowPasswordModal(false);
    setPasswordForm({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setCanCloseModal(true);
  };

  const handlePasswordInputChange = (field, value) => {
    setPasswordForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePasswordSubmit = async (e) => {
    // Prevenir comportamiento por defecto si viene de un form
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Validar que las contraseñas coincidan
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setAlert({
        show: true,
        message: 'Las contraseñas nuevas no coinciden',
        type: 'error'
      });
      return false; // No cerrar modal
    }

    // Validar que la contraseña nueva no esté vacía
    if (!passwordForm.newPassword.trim()) {
      setAlert({
        show: true,
        message: 'La nueva contraseña no puede estar vacía',
        type: 'error'
      });
      return false; // No cerrar modal
    }

    // Validar longitud mínima
    if (passwordForm.newPassword.length < 6) {
      setAlert({
        show: true,
        message: 'La nueva contraseña debe tener al menos 6 caracteres',
        type: 'error'
      });
      return false; // No cerrar modal
    }

    setPasswordLoading(true);
    setCanCloseModal(false); // Bloquear cierre durante procesamiento
    
    try {
      
      // Llamar al servicio para actualizar la contraseña
      await usuarioService.updatePassword(
        passwordForm.currentPassword,
        passwordForm.newPassword,
        passwordForm.confirmPassword
      );
      
      // ✅ ÉXITO: Cerrar modal directamente
      setShowPasswordModal(false);
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      // Mostrar mensaje de éxito
      setTimeout(() => {
        setAlert({
          show: true,
          message: 'Contraseña actualizada exitosamente',
          type: 'success'
        });
      }, 300);
      
      return true; // Éxito
      
    } catch (error) {
      console.error('❌ ERROR AL ACTUALIZAR CONTRASEÑA:', error);
      
      // ❌ ERROR: NO cerrar modal, solo mostrar error
      setAlert({
        show: true,
        message: error.message || 'Error al actualizar la contraseña',
        type: 'error'
      });
      
      return false; // Error - mantener modal abierto
      
    } finally {
      setPasswordLoading(false);
      setCanCloseModal(true);
    }
  };

  const closeAlert = () => {
    setAlert(prev => ({ ...prev, show: false }));
  };

  const menuItems = getProfessorMenuItems({ user, isGptClassicEnabled, isOpenAIAssistantEnabled, isRagFlowAssistantEnabled });

  // Generar tarjetas del dashboard con datos reales
  const getDashboardCards = () => {
    if (!datosFormateados) {
      return [
        { title: 'Asistentes IA', value: '-', icon: 'fas fa-robot', color: 'primary' },
        { title: 'Conversaciones Activas', value: '-', icon: 'fas fa-comments', color: 'secondary' },
      ];
    }

    const { resumenPrincipal } = datosFormateados;
    return [
      { 
        title: 'Asistentes IA', 
        value: resumenPrincipal.totalAsistentes?.toString() || '0', 
        icon: 'fas fa-robot', 
        color: 'primary',
        subtitle: `${resumenPrincipal.asistenteMasUsado !== 'Ninguno' ? `Más usado: ${resumenPrincipal.asistenteMasUsado}` : 'Sin uso registrado'}`
      },
      { 
        title: 'Conversaciones Este Mes', 
        value: resumenPrincipal.conversacionesMes?.toString() || '0', 
        icon: 'fas fa-comments', 
        color: 'secondary',
        subtitle: `Hoy: ${resumenPrincipal.conversacionesHoy || 0} | Semana: ${resumenPrincipal.conversacionesSemana || 0}`
      },
    ];
  };

  const handleNewAssistant = () => {
    if (!user?.pertmiteCrearAsistente) {
      setAlert({
        show: true,
        title: 'Permiso denegado',
        message: 'No tienes permisos para crear asistentes. Contacta al administrador.',
        type: 'warning'
      });
      return;
    }
    if (!isOpenAIAssistantEnabled) {
      setAlert({
        show: true,
        title: 'Funcionalidad deshabilitada',
        message: 'El feature flag de Asistentes OpenAI no está habilitado para esta regional.',
        type: 'warning'
      });
      return;
    }

    navigate('/new-assistant');
  };

  const handleNewRagFlowAssistant = () => {
    if (!user?.pertmiteCrearAsistente) {
      setAlert({
        show: true,
        title: 'Permiso denegado',
        message: 'No tienes permisos para crear asistentes. Contacta al administrador.',
        type: 'warning'
      });
      return;
    }
    if (!isRagFlowAssistantEnabled) {
      setAlert({
        show: true,
        title: 'Funcionalidad deshabilitada',
        message: 'El feature flag de Asistentes AVE no esta habilitado para esta regional.',
        type: 'warning'
      });
      return;
    }

    navigate('/new-ragflow-assistant');
  };

  const handleNewGptAssistant = () => {
    if (!user?.pertmiteCrearAsistente) {
      setAlert({
        show: true,
        title: 'Permiso denegado',
        message: 'No tienes permisos para crear asistentes. Contacta al administrador.',
        type: 'warning'
      });
      return;
    }
    if (!isGptClassicEnabled) {
      setAlert({
        show: true,
        title: 'Funcionalidad deshabilitada',
        message: 'El feature flag de Asistentes GPT no está habilitado para esta regional.',
        type: 'warning'
      });
      return;
    }

    navigate('/new-gpt-assistant');
  };
  const handleMenuItemClick = (itemId) => {
    setActiveMenuItem(itemId);
    setIsSidebarOpen(false);

    navigateProfessorMenuItem(navigate, itemId);
  };

  return (
    <div className={`new-dashboard ${isSidebarOpen ? 'sidebar-open' : ''}`}>
      {isSidebarOpen && <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />}
      {/* Sidebar */}
      <aside className={`dashboard-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-graduation-cap"></i>
            <span>UTN FRC</span>
          </div>
        </div>
        
        <nav className="sidebar-nav">
          <ProfessorMenuOptions
            user={user}
            isGptClassicEnabled={isGptClassicEnabled}
            isOpenAIAssistantEnabled={isOpenAIAssistantEnabled}
            isRagFlowAssistantEnabled={isRagFlowAssistantEnabled}
            activeItemId={activeMenuItem}
            onSelect={(item) => handleMenuItemClick(item.id)}
          />
        </nav>
        
        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
              <span className="user-role">{user?.rol || user?.role || 'Profesor'}</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="dashboard-main">
        {/* Header */}
        <header className="dashboard-header">
          <div className="header-left">
            <button
              type="button"
              className="sidebar-toggle"
              onClick={() => setIsSidebarOpen((prev) => !prev)}
              title="Alternar menú lateral"
            >
              <i className="fas fa-bars"></i>
            </button>
            <h1 className="page-title">
              {menuItems.find(item => item.id === activeMenuItem)?.label || 'Dashboard'}
            </h1>
          </div>
          
          <div className="header-right">
            <ActiveRegionalBadge />
            <SolicitudesProcesosBadge />
            <ProcesosMenu />
            <button className="logout-btn-header" onClick={handleLogout} title="Cerrar Sesión">
              <i className="fas fa-sign-out-alt"></i>
              <span className="logout-text">Salir</span>
            </button>
            
            <div className="user-menu">
              <button 
                className="user-menu-trigger"
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <div className="user-avatar">
                  <i className="fas fa-user"></i>
                </div>
                <i className="fas fa-chevron-down"></i>
              </button>
              
              {showUserMenu && (
                <div className="user-menu-dropdown">
                  <div className="dropdown-header">
                    <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
                    <span className="user-email">{user?.email || 'usuario@utn.edu.ar'}</span>
                  </div>
                  <div className="dropdown-divider"></div>
                  <button className="dropdown-item" onClick={handleUpdatePassword}>
                    <i className="fas fa-key"></i>
                    Actualizar Password
                  </button>
                  <div className="dropdown-divider"></div>
                  <button className="dropdown-item logout-item" onClick={handleLogout}>
                    <i className="fas fa-sign-out-alt"></i>
                    Cerrar Sesión
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="dashboard-content">
          {/* Alert */}
          {alert.show && (
            <Alert
              title={alert.title}
              message={alert.message}
              type={alert.type}
              onClose={closeAlert}
            />
          )}

          {/* Nuevo Asistente Button - Ubicado en la parte superior */}
          {user?.pertmiteCrearAsistente && (isOpenAIAssistantEnabled || isRagFlowAssistantEnabled || isGptClassicEnabled) && (
            <div className="new-assistant-section-top">
              {isOpenAIAssistantEnabled && (
                <button className="new-assistant-btn-top" onClick={handleNewAssistant}>
                  <div className="btn-icon">
                    <i className="fas fa-plus"></i>
                  </div>
                  <div className="btn-content">
                    <span className="btn-title">Nuevo Asistente OpenAI</span>
                    <span className="btn-subtitle">Crear un asistente OpenAI personalizado</span>
                  </div>
                </button>
              )}
              {isRagFlowAssistantEnabled && (
                <button className="new-assistant-btn-top new-assistant-btn-top--secondary" onClick={handleNewRagFlowAssistant}>
                  <div className="btn-icon">
                    <i className="fas fa-plus"></i>
                  </div>
                  <div className="btn-content">
                    <span className="btn-title">Nuevo Asistente AVE</span>
                    <span className="btn-subtitle">Crear un asistente AVE</span>
                  </div>
                </button>
              )}
              {isGptClassicEnabled && (
                <button className="new-assistant-btn-top new-assistant-btn-top--secondary" onClick={handleNewGptAssistant}>
                  <div className="btn-icon">
                    <i className="fas fa-plus"></i>
                  </div>
                  <div className="btn-content">
                    <span className="btn-title">Nuevo Asistente GPT</span>
                    <span className="btn-subtitle">Crear un asistente GPT clásico</span>
                  </div>
                </button>
              )}
            </div>
          )}

          {activeMenuItem === 'dashboard' && (
            <div className="dashboard-overview">
              {/* Loading Spinner */}
              {isLoading ? (
                <div className="loading-container">
                  <LoadingSpinner message="Obteniendo estadísticas del dashboard..." />
                </div>
              ) : (
                <>
                  {/* Stats Cards */}
                  <div className="stats-grid">
                    {getDashboardCards().map((card, index) => (
                      <div key={index} className={`stats-card ${card.color}`}>
                        <div className="card-icon">
                          <i className={card.icon}></i>
                        </div>
                        <div className="card-content">
                          <h3 className="card-value">{card.value}</h3>
                          <p className="card-title">{card.title}</p>
                          {card.subtitle && <p className="card-subtitle">{card.subtitle}</p>}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Gráfico de actividad de los últimos 7 días */}
                  {datosFormateados?.actividadSemanal?.length > 0 && (
                    <div className="dashboard-section">
                      <div className="section-header">
                        <h2 className="section-title">
                          <i className="fas fa-chart-line"></i>
                          Conversaciones de los Últimos 7 Días
                        </h2>
                      </div>
                      <div className="chart-container">
                          <div className="activity-chart">
                          {(() => {
                            const values = datosFormateados.actividadSemanal.map((d) => Number(d.conversaciones) || 0);
                            const maxConversaciones = Math.max(...values, 1);
                            return datosFormateados.actividadSemanal.map((dia, index) => {
                              const conversacionesValue = Number(dia.conversaciones) || 0;
                              const heightPct = (conversacionesValue / maxConversaciones) * 100;
                              return (
                                <div key={index} className="chart-bar">
                                  <div
                                    className="bar-fill"
                                    style={{
                                      height: `${Math.max(heightPct, 5)}%`
                                    }}
                                  ></div>
                                  <span className="bar-label">{dia.fecha}</span>
                                  <span className="bar-value">{conversacionesValue}</span>
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Métricas Adicionales */}
                  {tendencias && (
                    <div className="dashboard-section">
                      <div className="section-header">
                        <h2 className="section-title">
                          <i className="fas fa-analytics"></i>
                          Resumen de Tendencias
                        </h2>
                      </div>
                      <div className="metrics-grid">
                        <div className="metric-card">
                          <div className="metric-icon">
                            <i className="fas fa-chart-line"></i>
                          </div>
                          <div className="metric-content">
                            <h4>Promedio Diario</h4>
                            <span className="metric-value">{tendencias.promedioDiario}</span>
                            <small>conversaciones por día</small>
                          </div>
                        </div>
                        <div className="metric-card">
                          <div className="metric-icon">
                            <i className="fas fa-crown"></i>
                          </div>
                          <div className="metric-content">
                            <h4>Día Más Activo</h4>
                            <span className="metric-value">{tendencias.diaMasActivo.fecha}</span>
                            <small>{tendencias.diaMasActivo.conversaciones} conversaciones</small>
                          </div>
                        </div>
                        <div className="metric-card">
                          <div className="metric-icon">
                            <i className={`fas ${tendencias.tasaCrecimiento >= 0 ? 'fa-arrow-up text-success' : 'fa-arrow-down text-danger'}`}></i>
                          </div>
                          <div className="metric-content">
                            <h4>Crecimiento</h4>
                            <span className={`metric-value ${tendencias.tasaCrecimiento >= 0 ? 'text-success' : 'text-danger'}`}>
                              {tendencias.tasaCrecimiento >= 0 ? '+' : ''}{tendencias.tasaCrecimiento}%
                            </span>
                            <small>mes vs semana</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}



              {/* Recent Activity */}
              
            </div>
          )}

          {activeMenuItem !== 'dashboard' && (
            <div className="content-placeholder">
              <div className="placeholder-icon">
                <i className={menuItems.find(item => item.id === activeMenuItem)?.icon}></i>
              </div>
              <h2>Sección en Desarrollo</h2>
              <p>Esta sección estará disponible próximamente.</p>
            </div>
          )}
        </div>
      </main>

      {/* Modal de Cambio de Password */}
      {showPasswordModal && (
        <div className="modal-overlay" onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (canCloseModal && !passwordLoading) {
            handleClosePasswordModal();
          }
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                <i className="fas fa-key"></i>
                Actualizar Contraseña
              </h3>
              <button 
                className="modal-close-btn" 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (canCloseModal && !passwordLoading) {
                    handleClosePasswordModal();
                  }
                }}
                disabled={!canCloseModal || passwordLoading}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="currentPassword">Contraseña Actual</label>
                <PasswordInputWithToggle
                  id="currentPassword"
                  className="form-input"
                  value={passwordForm.currentPassword}
                  onChange={(e) => handlePasswordInputChange('currentPassword', e.target.value)}
                  placeholder="Ingresa tu contraseña actual"
                  disabled={passwordLoading}
                  label="contraseña actual"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="newPassword">Nueva Contraseña</label>
                <PasswordInputWithToggle
                  id="newPassword"
                  className="form-input"
                  value={passwordForm.newPassword}
                  onChange={(e) => handlePasswordInputChange('newPassword', e.target.value)}
                  placeholder="Ingresa tu nueva contraseña"
                  disabled={passwordLoading}
                  label="nueva contraseña"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="confirmPassword">Repetir Nueva Contraseña</label>
                <PasswordInputWithToggle
                  id="confirmPassword"
                  className="form-input"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => handlePasswordInputChange('confirmPassword', e.target.value)}
                  placeholder="Repite tu nueva contraseña"
                  disabled={passwordLoading}
                  label="repetir nueva contraseña"
                />
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn btn-secondary" 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (canCloseModal && !passwordLoading) {
                    handleClosePasswordModal();
                  }
                }}
                disabled={!canCloseModal || passwordLoading}
              >
                Cancelar
              </button>
              <button 
                className="btn btn-primary" 
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handlePasswordSubmit(e);
                }}
                disabled={passwordLoading}
              >
                {passwordLoading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Actualizando...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save"></i>
                    Actualizar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default NewDashboard;
